import { usePayTokenPriceLatest } from '@ui/features/Trade/hooks/usePayTokenPriceLatest';
import { usePriceLatest } from '@ui/hooks/price/usePriceLatest';
import { useTradePairInfos } from '@ui/hooks/trade/useTradePairInfos';
import { Position } from '@ui/services/contract/hooks/TradingReaderFacet/useGetPositionV2/types';
import { TradePair } from '@ui/types/tradePair';
import { getLeverageMargin } from '@ui/utils/trade/getLeverageMargin';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';

import { useLeverageChange } from './useLeverageChange';
import { useMaxRemovableCollateral } from './useMaxRemovableCollateral';
import { useWithdrawCollateralInput } from './useWithdrawCollateralInput';

const getLiquidationPriceForCollateral = ({
  position,
  pairInfo,
  collateralMarketPrice,
  pairMarketPrice,
  countableCollateralUsd,
  leverageForTier,
}: {
  position: Position;
  pairInfo: TradePair;
  collateralMarketPrice: BigNumber;
  pairMarketPrice: BigNumber;
  /** Remaining collateral in USD; passed straight through, not reconstructed. */
  countableCollateralUsd: BigNumber;
  /** Entry-notional leverage, only to pick the liqLostP tier (app convention). */
  leverageForTier: BigNumber;
}) => {
  const liqLostP = getLeverageMargin({
    leverage: leverageForTier.toNumber(),
    leverageMargins: pairInfo.leverageMargins,
  })?.liqLostP;

  if (!liqLostP || !pairInfo.feeConfig || countableCollateralUsd.lte(0)) {
    return undefined;
  }

  return position.getLiquidationPrice({
    collateralMarketPrice,
    pairMarketPrice,
    feeConfig: pairInfo.feeConfig,
    liqLostP,
    tradePair: pairInfo,
    override: {
      collateralUsd: countableCollateralUsd,
    },
  });
};

export const useLiquidationPriceChange = ({
  position,
}: {
  position: Position;
}) => {
  const price = usePayTokenPriceLatest(position.collateralToken);
  const pairPrice = usePriceLatest(position.pair.baseSymbol);
  const leverageChange = useLeverageChange({ position });
  const maxRemovableResult = useMaxRemovableCollateral({ position });
  const inputToken = useWithdrawCollateralInput((s) => s.input.value.token);
  const pairInfos = useTradePairInfos();

  const pairInfo = useMemo(() => {
    return pairInfos?.find(
      (v) => v.address.toLowerCase() === position.pair.address.toLowerCase(),
    );
  }, [pairInfos, position.pair.address]);

  const hasWithdrawInput = !!inputToken && inputToken.gt(0);

  return useMemo(() => {
    if (!price || !pairPrice || !leverageChange || !pairInfo?.leverageMargins) {
      return;
    }

    const prevCollateralUsd = position.getCollateralUsd(price);

    const prev = getLiquidationPriceForCollateral({
      position,
      pairInfo,
      collateralMarketPrice: price,
      pairMarketPrice: pairPrice,
      countableCollateralUsd: prevCollateralUsd,
      leverageForTier: leverageChange.prev,
    });

    const nextFromInput =
      hasWithdrawInput && inputToken
        ? getLiquidationPriceForCollateral({
            position,
            pairInfo,
            collateralMarketPrice: price,
            pairMarketPrice: pairPrice,
            countableCollateralUsd: prevCollateralUsd.minus(
              inputToken.times(price),
            ),
            leverageForTier: leverageChange.next,
          })
        : undefined;

    const isCappedByLiquidation =
      maxRemovableResult?.limitReason === 'liquidation' &&
      maxRemovableResult.maxRemovable.gt(0);

    const nextAtMaxRemovable =
      isCappedByLiquidation && maxRemovableResult.leverageAfterMaxRemoval
        ? getLiquidationPriceForCollateral({
            position,
            pairInfo,
            collateralMarketPrice: price,
            pairMarketPrice: pairPrice,
            countableCollateralUsd: position.collateral
              .minus(maxRemovableResult.maxRemovable)
              .times(price),
            leverageForTier: maxRemovableResult.leverageAfterMaxRemoval,
          })
        : undefined;

    return {
      prev,
      next: nextFromInput,
      nextAtMaxRemovable,
    };
  }, [
    hasWithdrawInput,
    inputToken,
    leverageChange,
    maxRemovableResult,
    pairInfo,
    pairPrice,
    position,
    price,
  ]);
};
